import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';

export const getUsers = createAsyncThunk('users/getUsers', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/users');

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані користувачів');
  }
});

export const getUser = createAsyncThunk('users/getUser', async (userID, { rejectWithValue }) => {
  try {
    const response = await api.get(`/users/${userID}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані користувача');
  }
});

export const updateUserRoles = createAsyncThunk('users/updateUserRoles', async (changedRoles, { rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.put('/users/roles', changedRoles, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Помилка оновлення ролей користувачів');
  }
});

export const updateUserLocations = createAsyncThunk(
  'users/updateUserLocations',
  async (changedLocations, { rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put('/users/locations', changedLocations, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка оновлення локацій користувачів');
    }
  }
);

export const deleteUsers = createAsyncThunk('users/deleteUsers', async (userIds, { rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.delete('/users', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { ids: userIds },
    });

    return response.data?.deletedIds;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося видалити користувачів');
  }
});

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    statuses: {},
    error: null,
    isLoading: false,
  },
  reducers: {
    updateUserStatus: (state, action) => {
      const { id, isOnline, lastSeen } = action.payload;
      state.statuses[id] = { isOnline, lastSeen };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.fulfilled, (state, action) => {
        action.payload.forEach((user) => {
          state.statuses[user.id] = {
            isOnline: user.isOnline,
            lastSeen: user.lastSeen,
          };
        });
        state.users = action.payload;
        state.error = null;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.error = action.payload || 'Помилка';
      })
      .addCase(getUser.fulfilled, (state, action) => {
        const user = action.payload;
        const existingIndex = state.users.findIndex((u) => u.id === user.id);

        if (existingIndex !== -1) {
          state.users[existingIndex] = user;
        } else {
          state.users.unshift(user);
        }

        state.statuses[user.id] = {
          isOnline: user.isOnline,
          lastSeen: user.lastSeen,
        };
      })
      .addCase(getUser.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані користувача';
      })
      .addCase(updateUserRoles.fulfilled, (state, action) => {
        const updatedUsers = action.payload?.data || [];

        updatedUsers.forEach((updatedUser) => {
          const index = state.users.findIndex((user) => user.id === updatedUser.id);
          if (index !== -1) {
            state.users[index] = updatedUser;
          }
        });

        state.error = null;
      })
      .addCase(updateUserRoles.rejected, (state, action) => {
        state.error = action.payload || 'Помилка';
      })
      .addCase(updateUserLocations.fulfilled, (state, action) => {
        const updatedUsers = action.payload?.data || [];

        updatedUsers.forEach((updatedUser) => {
          const index = state.users.findIndex((user) => user.id === updatedUser.id);
          if (index !== -1) {
            state.users[index] = updatedUser;
          }
        });

        state.error = null;
      })
      .addCase(updateUserLocations.rejected, (state, action) => {
        state.error = action.payload || 'Помилка';
      })
      .addCase(deleteUsers.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => !action.payload.includes(user.id));
      })
      .addCase(deleteUsers.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося видалити користувачів';
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const { updateUserStatus } = usersSlice.actions;
export default usersSlice.reducer;
